import { apiErrorMessage } from '@/utils/apiError'
import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { api } from '@/services/api'
import { useAuthStore } from '@/stores/auth'
import { resolveMediaUrl } from '@/utils/media'
import { track } from '@/services/analytics'
import type { Listing, ListingSummary, Category, ListingsQuery } from '@/types/api'

function normalizeListing<T extends { images?: string[] }>(item: T): T {
  if (Array.isArray(item.images)) {
    return {
      ...item,
      images: item.images.map(resolveMediaUrl),
    }
  }
  return item
}

export const useListingsStore = defineStore('listings', () => {
  const authStore = useAuthStore()
  // State
  const listings = ref<ListingSummary[]>([])
  const currentListing = ref<Listing | null>(null)
  const categories = ref<Category[]>([])
  const isLoading = ref(false)
  const error = ref<string | null>(null)
  
  // Pagination
  const currentPage = ref(1)
  const totalPages = ref(1)
  const totalItems = ref(0)
  const hasMore = computed(() => currentPage.value < totalPages.value)
  
  // Filters
  const filters = ref<ListingsQuery>({
    page: 1,
    limit: 20,
    sort: 'newest',
  })

  let listRequest = 0

  // Actions
  async function fetchListings(append = false) {
    if (append && isLoading.value) return
    const request = ++listRequest
    
    isLoading.value = true
    error.value = null
    
    try {
      const page = append ? currentPage.value + 1 : 1
      const params = { ...filters.value, page }
      
      const response = await api.getListings(params)
      if (request !== listRequest) return
      
      const items = (response.data || []).map(normalizeListing)
      if (append) {
        listings.value.push(...items)
      } else {
        listings.value = items
      }
      
      currentPage.value = response.page
      totalPages.value = response.totalPages
      totalItems.value = response.total
    } catch (err: any) {
      if (request !== listRequest) return
      error.value = apiErrorMessage(err, 'Failed to load listings')
      if (err.response?.status !== 401) {
        authStore.addToast('error', error.value)
      }
    } finally {
      if (request === listRequest) isLoading.value = false
    }
  }

  // Detail/actions have their own page loading state; they must not cancel a browse request.
  async function fetchListing(id: string) {
    const raw = await api.getListing(id)
    currentListing.value = normalizeListing(raw)
    return currentListing.value
  }

  async function fetchOwnListing(id: string) {
    currentListing.value = normalizeListing(await api.getOwnListing(id))
    return currentListing.value
  }

  async function createListing(payload: Parameters<typeof api.createListing>[0]) {
    const listing = normalizeListing(await api.createListing(payload))
    listings.value.unshift(listing)
    track('Listing Published', { listing_id: listing.id, category_id: payload.categoryID, price_pi: payload.pricePi })
    return listing
  }

  async function updateListing(id: string, payload: Parameters<typeof api.updateListing>[1]) {
    const listing = normalizeListing(await api.updateListing(id, payload))
    replaceListing(listing)
    return listing
  }

  async function deleteListing(id: string) {
    await api.deleteListing(id)
    listings.value = listings.value.filter(l => l.id !== id)
  }

  async function pauseListing(id: string) {
    const listing = await api.pauseListing(id)
    replaceListing(listing)
    return listing
  }

  function replaceListing(listing: Listing) {
    const index = listings.value.findIndex(l => l.id === listing.id)
    if (index >= 0) listings.value[index] = listing
    if (currentListing.value?.id === listing.id) currentListing.value = listing
  }

  async function fetchCategories() {
    try {
      categories.value = await api.getCategories()
    } catch (err: any) {
      console.error('Failed to load categories:', err)
    }
  }

  function setFilters(newFilters: Partial<ListingsQuery>) {
    filters.value = { ...filters.value, ...newFilters, page: 1 }
  }

  function clearFilters() {
    filters.value = {
      page: 1,
      limit: 20,
      sort: 'newest',
    }
  }

  function clearCurrentListing() {
    currentListing.value = null
  }

  return {
    // State
    listings,
    currentListing,
    categories,
    isLoading,
    error,
    currentPage,
    totalPages,
    totalItems,
    hasMore,
    filters,
    // Actions
    fetchListings,
    fetchListing,
    createListing,
    updateListing,
    fetchOwnListing,
    deleteListing,
    pauseListing,
    fetchCategories,
    setFilters,
    clearFilters,
    clearCurrentListing,
  }
})
